import { useState, useRef, useCallback, useEffect } from 'react'
import { ImageResult } from '../types'
import { useLoading } from '../editor/loading_state/useLoading'
import { aspectOptions } from '../dimension_management/DimensionOptions'

// Loading key constant
const disclaimerTextLoadingKey = 'disclaimer-text'

// Centralized scaling parameters with improved relationships
const scalingParams = {
    // Base parameters
    baseImageSize: 1024,

    // Textbox parameters (as percentages of image)
    textBoxHeightPercent: 0.1, // Changed back to 10% of image height

    // Font parameters (relative to textbox)
    fontSizeRatio: 0.25, // Font size as percentage of textbox height
    minFontSizeRatio: 0.15, // Minimum font size as percentage of textbox height

    // Padding (as percentage of textbox)
    paddingPercent: 0.12, // Reduced from 0.15 for better text fitting
    verticalPaddingScaleFactor: 1.0, // How vertical padding scales with font (1.0 = same rate)

    // Base padding for a standard sized text box (1024px image)
    basePadding: 12, // Fixed padding value for standard size

    // Minimum padding in pixels (to ensure there's always some margin)
    minPaddingPixels: 5, // Changed from 10px to 5px
    minVerticalPaddingPixels: 5, // Can be smaller than horizontal padding

    // Line height (as multiple of font size)
    lineHeightRatio: 1.0,

    // Scale factor (applied to font size)
    defaultScaleFactor: 1.0,
    minScaleFactor: 0.7,
    maxScaleFactor: 1.5,
    scaleStep: 0.05, // Amount to increase/decrease scale by

    // Colors
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    textColor: '#D3D3D3',
    fontFamily: 'Arial',

    // Text wrapping
    maxLinesBeforeScale: 3, // Base number of lines before scaling
    lineScaleFactor: 1.5 // How many additional lines to allow for each 0.1 scale increase
}

interface UseDisclaimerTextOptions {
    maxLength?: number
    onTextChange?: (text: string) => void
    imageWidth?: number
    imageHeight?: number
    onAccept?: (result: ImageResult) => void
    imageCanvasRef?: React.RefObject<HTMLCanvasElement>
    dimensionImages?: Record<string, { url: string; width: number; height: number }>
    onMultiDimensionAccept?: (results: Record<string, ImageResult>) => void
    textAlignment?: 'left' | 'center'
    initialScaleFactor?: number
}

interface UseDisclaimerTextReturn {
    text: string
    setText: (text: string) => void
    isValid: boolean
    previewRef: React.RefObject<HTMLCanvasElement>
    imageCanvasRef: React.RefObject<HTMLCanvasElement> | undefined
    handleAccept: () => Promise<void>
    handleAcceptAllDimensions: () => Promise<void>
    isProcessing: boolean
    textConfig: {
        fontSize: number
        padding: number
        lineHeight: number
        textBoxHeight: number
        lines: string[]
        hasText: boolean
        yOffset: number
        linePositions: number[]
        verticalPadding: number
    }
    textAlignment: 'left' | 'center'
    setTextAlignment: (alignment: 'left' | 'center') => void
    scaleFactor: number
    increaseScale: () => void
    decreaseScale: () => void
}

/**
 * Hook for managing disclaimer text state and rendering
 */
export const useDisclaimerText = ({
    maxLength = 300,
    onTextChange,
    imageWidth = 0,
    imageHeight = 0,
    onAccept,
    imageCanvasRef,
    dimensionImages = {},
    onMultiDimensionAccept,
    textAlignment: initialTextAlignment = 'left',
    initialScaleFactor = 1.0,
}: UseDisclaimerTextOptions = {}): UseDisclaimerTextReturn => {
    const [text, setText] = useState('')
    const [textAlignment, setTextAlignment] = useState<'left' | 'center'>(initialTextAlignment)
    const [scaleFactor, setScaleFactor] = useState(
        Math.min(
            Math.max(initialScaleFactor, scalingParams.minScaleFactor),
            scalingParams.maxScaleFactor
        )
    )
    const previewRef = useRef<HTMLCanvasElement>(null)
    const dimensionsRef = useRef({ width: imageWidth, height: imageHeight })
    const tempCanvasRef = useRef<HTMLCanvasElement | null>(null)

    // Replace isProcessing state with useLoading hook
    const { startLoading, stopLoading, isLoading } = useLoading(disclaimerTextLoadingKey)

    // Functions to increase/decrease scale factor
    const increaseScale = useCallback(() => {
        setScaleFactor(prevScale => {
            const newScale = prevScale + scalingParams.scaleStep
            return Math.min(newScale, scalingParams.maxScaleFactor)
        })
    }, [])

    const decreaseScale = useCallback(() => {
        setScaleFactor(prevScale => {
            const newScale = prevScale - scalingParams.scaleStep
            return Math.max(newScale, scalingParams.minScaleFactor)
        })
    }, [])

    // Create temporary canvas for text measurements
    useEffect(() => {
        if (!tempCanvasRef.current) {
            tempCanvasRef.current = document.createElement('canvas')
        }
        return () => {
            tempCanvasRef.current = null
        }
    }, [])

    // Calculate text wrapping and line breaks
    const calculateTextLines = useCallback((
        inputText: string,
        fontSize: number,
        maxWidth: number
    ): string[] => {
        if (!tempCanvasRef.current) return inputText.split('\n')

        const ctx = tempCanvasRef.current.getContext('2d')
        if (!ctx) return inputText.split('\n')

        // Set font with proper size
        ctx.font = `${fontSize}px ${scalingParams.fontFamily}`

        // Use actual image width for text wrapping
        const availableWidth = maxWidth
        const lines: string[] = []

        // Split text into paragraphs (manual line breaks)
        const paragraphs = inputText.split('\n')

        paragraphs.forEach(paragraph => {
            if (paragraph.trim() === '') {
                lines.push('')
                return
            }

            const words = paragraph.split(' ')
            let currentLine = words[0] || ''

            for (let i = 1; i < words.length; i++) {
                const word = words[i]
                const testLine = currentLine + ' ' + word
                const width = ctx.measureText(testLine).width

                if (width <= availableWidth) {
                    currentLine = testLine
                } else {
                    lines.push(currentLine)
                    currentLine = word
                }
            }
            if (currentLine) {
                lines.push(currentLine)
            }
        })

        return lines
    }, [])

    // Calculate scaled values based on image dimensions and text content
    const calculateScaledValues = useCallback((width: number, height: number, inputText: string) => {
        // Use fixed padding values that don't scale with font size
        const padding = Math.max(scalingParams.basePadding, scalingParams.minPaddingPixels)
        const verticalPadding = Math.max(scalingParams.basePadding, scalingParams.minVerticalPaddingPixels)

        // Calculate the maximum available text box height (this is just for initial calculations)
        const maxTextBoxHeight = height * scalingParams.textBoxHeightPercent * scaleFactor

        // Calculate the actual text area width (constant regardless of font size)
        const textAreaWidth = width - (padding * 2)

        // Calculate initial font size based on the maximum textbox height
        const initialFontSize = maxTextBoxHeight * scalingParams.fontSizeRatio
        const minFontSize = maxTextBoxHeight * scalingParams.minFontSizeRatio

        // Calculate max lines based on scale factor:
        // - At scaleFactor <= 1.0: use the base maxLinesBeforeScale (3)
        // - At scaleFactor > 1.0: allow more lines proportional to the scale increase
        const scaleIncrease = Math.max(0, scaleFactor - 1.0);
        const additionalLines = Math.floor(scaleIncrease / scalingParams.scaleStep * scalingParams.lineScaleFactor);
        const adjustedMaxLines = scalingParams.maxLinesBeforeScale + additionalLines;

        // Binary search for optimal font size
        let low = minFontSize;
        let high = initialFontSize;
        let fontSize = low; // Default to minimum if nothing fits

        // Use a smaller precision threshold (0.1px instead of 0.5px) for more granular font size changes
        const precisionThreshold = 0.1;

        while (high - low > precisionThreshold) {
            const mid = (low + high) / 2;

            // Actually test if the text fits at this font size
            const testLines = calculateTextLines(inputText, mid, textAreaWidth);
            const fitsMaxLines = testLines.length <= adjustedMaxLines;

            if (fitsMaxLines) {
                // If it fits, try a larger font size
                fontSize = mid;
                low = mid;
            } else {
                // If it doesn't fit, try a smaller font size
                high = mid;
            }
        }

        // Use the largest font size that fits
        fontSize = low;
        const lineHeight = fontSize * scalingParams.lineHeightRatio;
        let lines = calculateTextLines(inputText, fontSize, textAreaWidth);

        // Enforce maximum line count if necessary
        if (lines.length > adjustedMaxLines) {
            lines = lines.slice(0, adjustedMaxLines);
        }

        // Calculate additional display properties
        const hasText = lines.some(line => line.trim() !== '');

        // Calculate the actual textbox height based on the number of lines
        // Only make the text box as tall as needed for the actual text content
        const actualTextBoxHeight = hasText
            ? (lines.length * lineHeight) + (verticalPadding * 2)
            : 0;

        // Calculate yOffset from the bottom of the image
        const yOffset = height - actualTextBoxHeight;

        // Calculate line positions, starting from the vertical padding
        const linePositions = lines.map((_, index) =>
            verticalPadding + (index * lineHeight)
        );

        return {
            fontSize,
            padding,
            lineHeight,
            textBoxHeight: actualTextBoxHeight, // Use dynamic height
            lines,
            hasText,
            yOffset,
            linePositions,
            verticalPadding
        }
    }, [calculateTextLines, scaleFactor])

    // Handle text change with validation
    const handleTextChange = useCallback((newText: string) => {
        // Enforce maxLength
        if (newText.length > maxLength) {
            newText = newText.substring(0, maxLength)
        }
        setText(newText)
        onTextChange?.(newText)
    }, [onTextChange, maxLength])

    // Clear the preview canvas when text is empty
    useEffect(() => {
        if (!text && previewRef.current) {
            const ctx = previewRef.current.getContext('2d')
            if (ctx) {
                ctx.clearRect(0, 0, dimensionsRef.current.width, dimensionsRef.current.height)
            }
        }
    }, [text, dimensionsRef, previewRef])

    // Helper function to apply disclaimer text to an image
    const applyDisclaimerToImage = useCallback(async (
        imageUrl: string,
        width: number,
        height: number
    ): Promise<ImageResult> => {
        // Create canvas for compositing
        const tempCanvas = document.createElement('canvas')
        tempCanvas.width = width
        tempCanvas.height = height
        const ctx = tempCanvas.getContext('2d')
        if (!ctx) throw new Error('Failed to get canvas context')

        // Step 1: Draw original image
        const originalImage = new Image()
        await new Promise<void>((resolve, reject) => {
            originalImage.onload = () => {
                ctx.drawImage(originalImage, 0, 0, width, height)
                resolve()
            }
            originalImage.onerror = () => reject(new Error('Failed to load original image'))
            originalImage.src = imageUrl
        })

        // Step 2: Calculate text config for this specific dimension
        const textConfig = calculateScaledValues(width, height, text)

        // Step 3: Draw text directly to canvas
        if (textConfig.hasText) {
            // Draw background
            ctx.fillStyle = scalingParams.backgroundColor
            ctx.fillRect(0, textConfig.yOffset, width, textConfig.textBoxHeight)

            // Draw text
            ctx.fillStyle = scalingParams.textColor
            ctx.font = `${textConfig.fontSize}px ${scalingParams.fontFamily}`
            ctx.textBaseline = 'top'

            textConfig.lines.forEach((line, index) => {
                const x = textAlignment === 'center' ? width / 2 : textConfig.padding
                const y = textConfig.yOffset + textConfig.linePositions[index]

                if (textAlignment === 'center') {
                    ctx.textAlign = 'center'
                } else {
                    ctx.textAlign = 'left'
                }

                ctx.fillText(line, x, y)
            })
        }

        // Step 4: Create final result
        const result = {
            url: tempCanvas.toDataURL('image/png'),
            width,
            height,
            cleanup: () => {
                // No cleanup needed with Canvas approach, but we need a function to satisfy the interface
                return
            }
        }

        return result
    }, [text, textAlignment, calculateScaledValues])

    // Handle applying disclaimer text to all dimension images
    const handleAcceptAllDimensions = useCallback(async () => {
        if (!text || isLoading() || Object.keys(dimensionImages).length === 0) {
            return;
        }

        try {
            startLoading('Adding disclaimer text to all images...', 1) // Priority 1 for image editing

            // Process all dimension images
            const results: Record<string, ImageResult> = {}

            // Process each dimension image
            for (const [dimensionId, imageData] of Object.entries(dimensionImages)) {
                // Apply disclaimer text to this dimension image
                const result = await applyDisclaimerToImage(
                    imageData.url,
                    imageData.width,
                    imageData.height
                )

                // Store the result with dimension ID
                results[dimensionId] = {
                    ...result,
                    dimensionId // Add dimension ID to the result
                }
            }

            // If we have the current canvas, update it with the current dimension result
            if (imageCanvasRef?.current) {
                const currentDimensionId = aspectOptions.find(
                    option => option.targetWidth === dimensionsRef.current.width &&
                    option.targetHeight === dimensionsRef.current.height
                )?.id ?? 'option1'

                const currentResult = results[currentDimensionId]

                if (currentResult) {
                    const image = new Image()
                    image.onload = () => {
                        const imageCtx = imageCanvasRef.current?.getContext('2d')
                        if (imageCtx) {
                            imageCtx.clearRect(0, 0, currentResult.width, currentResult.height)
                            imageCtx.drawImage(image, 0, 0)
                        }
                    }
                    image.src = currentResult.url
                }
            }

            // Clean up and notify
            setText('')
            onMultiDimensionAccept?.(results)

        } catch (error) {
            // Error handling is done by the parent component
        } finally {
            stopLoading()
        }
    }, [
        text,
        isLoading,
        dimensionImages,
        applyDisclaimerToImage,
        imageCanvasRef,
        dimensionsRef,
        onMultiDimensionAccept,
        setText,
        startLoading,
        stopLoading
    ])

    // Handle text burning accept for current image
    const handleAccept = useCallback(async () => {
        // Instead of applying to just the current image, we'll apply to all dimensions
        // This ensures that when the user clicks "Add Disclaimers", it's applied to all three dimension options
        // First try to apply to all dimensions
        await handleAcceptAllDimensions();

        // If onAccept is provided, also call it with the current dimension result
        if (onAccept && imageCanvasRef?.current) {
            try {
                // Get the current canvas dimensions
                const width = dimensionsRef.current.width;
                const height = dimensionsRef.current.height;

                // Create a data URL from the current canvas
                const imageUrl = imageCanvasRef.current.toDataURL('image/png');

                // Apply disclaimer text to the current image
                const result = await applyDisclaimerToImage(imageUrl, width, height);

                // Notify with the single result
                onAccept(result);
            } catch (error) {
                // Error handling is done by the parent component
            }
        }

        return;
    }, [handleAcceptAllDimensions, onAccept, imageCanvasRef, dimensionsRef, applyDisclaimerToImage]);

    useEffect(() => {
        if (imageWidth && imageHeight && (imageWidth !== dimensionsRef.current.width || imageHeight !== dimensionsRef.current.height)) {
            dimensionsRef.current = { width: imageWidth, height: imageHeight }
        }
    }, [imageWidth, imageHeight])

    // Update the text state and notify parent component
    useEffect(() => {
        onTextChange?.(text)
    }, [text, onTextChange])

    // Add an effect to preview the text on all dimensions whenever it changes
    useEffect(() => {
        // Skip if there's no text or no dimension images
        if (!text || Object.keys(dimensionImages).length === 0 || isLoading()) {
            return;
        }

        // Use a debounce to avoid too many previews
        const debounceTimeout = setTimeout(async () => {
            try {
                // No need to show loading state for automatic previews

                // Process all dimension images
                const results: Record<string, ImageResult> = {}

                // Calculate the current dimension ID
                const currentDimensionId = aspectOptions.find(
                    option => option.targetWidth === dimensionsRef.current.width &&
                    option.targetHeight === dimensionsRef.current.height
                )?.id ?? 'option1'

                // Process each dimension image but only apply to canvas for the current dimension
                for (const [dimensionId, imageData] of Object.entries(dimensionImages)) {
                    // Skip processing if this isn't the current dimension - we'll only calculate for the current dimension
                    // This prevents unnecessary calculations and potential double rendering
                    if (dimensionId !== currentDimensionId) {
                        continue;
                    }

                    // Apply disclaimer text to this dimension image
                    const result = await applyDisclaimerToImage(
                        imageData.url,
                        imageData.width,
                        imageData.height
                    )

                    // Store the result with dimension ID
                    results[dimensionId] = {
                        ...result,
                        dimensionId // Add dimension ID to the result
                    }
                }

                // If we have the current canvas, update it with the current dimension result
                if (imageCanvasRef?.current) {
                    const currentResult = results[currentDimensionId]

                    if (currentResult) {
                        // We don't need to update the actual canvas with the preview
                        // The DisclaimerTextCanvasPreview component will handle the preview rendering
                        // This prevents double rendering issues

                        // DO NOT update the actual canvas here - this was causing the double rendering
                        // The actual canvas should only be updated when the user clicks "Accept"
                    }
                }
            } catch (error) {
                // Error handling is done by the parent component
            }
        }, 300); // 300ms debounce

        // Clean up the timeout
        return () => clearTimeout(debounceTimeout);
    }, [text, dimensionImages, imageCanvasRef, applyDisclaimerToImage, isLoading, dimensionsRef]);

    const textConfig = calculateScaledValues(dimensionsRef.current.width, dimensionsRef.current.height, text)

    return {
        text,
        setText: handleTextChange,
        isValid: text.length > 0 && text.length <= maxLength,
        previewRef,
        imageCanvasRef,
        handleAccept,
        handleAcceptAllDimensions,
        isProcessing: isLoading(),
        textConfig,
        textAlignment,
        setTextAlignment,
        scaleFactor,
        increaseScale,
        decreaseScale
    }
}
